import { AccountTreeOutlined, TuneRounded } from "@mui/icons-material";
import { Box, Divider, Stack, Typography } from "@mui/material";
import type { ComparisonGroup } from "../../utils/llmClassifierVersionComparisonGroups";
import { CountChip, DiffBody } from "./DiffBlockCard";
import { MetadataChangeRows } from "./MetadataChangeRows";

const KIND_LABEL: Record<NonNullable<ComparisonGroup["kind"]>, string> = {
    changed: "",
    added: "Added",
    removed: "Removed",
    renamed: "Renamed",
};

/** Everything that changed about one step, or about the model itself. */
export function ChangeGroupCard(props: {
    group: ComparisonGroup;
    beforeLabel: string;
    afterLabel: string;
}) {
    const { group } = props;
    const kindLabel = group.kind ? KIND_LABEL[group.kind] : "";
    const isEmpty = group.diffs.length === 0 && group.metadata.length === 0;
    const heading = group.stepNumber ? `Step ${group.stepNumber}` : group.title;
    const name = group.title.trim();
    // Only show the name if different from the heading - avoids "Step 1 · Step 1" but shows "Step 1 · My Step Name"
    const showName =
        !!group.stepNumber &&
        name.length > 0 &&
        name.toLowerCase() !== heading.toLowerCase();

    if (isEmpty) return null;

    return (
        <Box
            sx={(theme) => ({
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: 2,
                backgroundColor: "white.main",
                overflow: "hidden",
            })}
        >
            <Stack
                direction="row"
                alignItems="center"
                gap={1}
                px={2}
                py={1.5}
                borderBottom={1}
                borderColor="gray.200"
            >
                <AccountTreeOutlined
                    fontSize="small"
                    sx={{ color: "gray.600" }}
                />
                <Typography fontSize="15px" fontWeight={600}>
                    {heading}
                </Typography>
                {showName && (
                    <Typography color="gray.500" variant="body2">
                        · {name}
                    </Typography>
                )}
                {kindLabel && (
                    <Typography color="gray.500" variant="body2">
                        · {kindLabel}
                    </Typography>
                )}
                <Stack direction="row" gap={1} ml="auto">
                    <CountChip count={group.removedLines} tone="removed" />
                    <CountChip count={group.addedLines} tone="added" />
                </Stack>
            </Stack>

            {group.diffs.map((diff, index) => (
                <Box key={diff.key}>
                    {index > 0 && <Divider />}
                    <Typography variant="body2" fontWeight={600} px={2} py={1}>
                        {diff.label}
                    </Typography>
                    <DiffBody
                        beforeText={diff.beforeText}
                        afterText={diff.afterText}
                        beforeLabel={props.beforeLabel}
                        afterLabel={props.afterLabel}
                    />
                </Box>
            ))}

            {group.metadata.length > 0 && (
                <>
                    {group.diffs.length > 0 && <Divider />}
                    <Box px={2} py={1.5}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            mb={1.5}
                        >
                            <TuneRounded
                                fontSize="small"
                                sx={{ color: "gray.600" }}
                            />
                            <Typography variant="body2" fontWeight={600}>
                                Metadata changes
                            </Typography>
                        </Stack>
                        <MetadataChangeRows changes={group.metadata} />
                    </Box>
                </>
            )}
        </Box>
    );
}
